import { Suspense, createRoot } from '@wordpress/element';

type BlockProps<
  TProps extends Record<string, unknown>,
  TAttribute extends Record<string, unknown>
> = TProps & {
  attributes?: TAttribute;
};

type BlockType<
  TProps extends Record<string, unknown>,
  TAttribute extends Record<string, unknown>
> = (props: BlockProps<TProps, TAttribute>) => JSX.Element | null;

export type GetPropsFn<
  TProps extends Record<string, unknown>,
  TAttributes extends Record<string, unknown>
> = (el: HTMLElement, i: number) => BlockProps<TProps, TAttributes>;

interface RenderBlockParams<
  TProps extends Record<string, unknown>,
  TAttributes extends Record<string, unknown>
> {
  // React component to use as a replacement.
  Block: BlockType<TProps, TAttributes> | null;
  // Container to replace with the Block component.
  container: HTMLElement;
  // Attributes object for the block.
  attributes: TAttributes;
  // Props object for the block.
  props: BlockProps<TProps, TAttributes>;
}

export const renderBlock = <
  TProps extends Record<string, unknown>,
  TAttributes extends Record<string, unknown>
>({
  Block,
  container,
  attributes = {} as TAttributes,
  props = {} as BlockProps<TProps, TAttributes>,
}: RenderBlockParams<TProps, TAttributes>): void => {
  const root = createRoot(container);
  root.render(
    <Suspense fallback={<div className="block-placeholder" />}>
      {Block && (
        <Block {...props} attributes={attributes} children={props.children} />
      )}
    </Suspense>
  );

  if (container.classList) {
    container.classList.remove('is-loading');
  }
};

interface RenderBlockInContainersParams<
  TProps extends Record<string, unknown>,
  TAttributes extends Record<string, unknown>
> {
  // React component to use as a replacement.
  Block: BlockType<TProps, TAttributes> | null;
  // Containers to replace with the Block component.
  containers: NodeListOf<Element>;
  // Function to generate the props object for the block.
  getProps?: GetPropsFn<TProps, TAttributes>;
  // Function to generate the props object for the error boundary.
  getErrorBoundaryProps?: (
    el: HTMLElement,
    i: number
  ) => Record<string, unknown>;
}

/**
 * Renders a block component in each `containers` node.
 */
const renderBlockInContainers = <
  TProps extends Record<string, unknown>,
  TAttributes extends Record<string, unknown>
>({
  Block,
  containers,
  getProps = () => ({} as BlockProps<TProps, TAttributes>),
}: RenderBlockInContainersParams<TProps, TAttributes>): void => {
  if (containers.length === 0) {
    return;
  }

  Array.prototype.forEach.call(containers, (el, i) => {
    const props = getProps(el, i);

    const attributes = {
      ...el.dataset,
      ...(props.attributes || {}),
    };

    renderBlock({
      Block,
      container: el,
      props,
      attributes,
    });
  });
};

interface RenderFrontendParams<
  TProps extends Record<string, unknown>,
  TAttributes extends Record<string, unknown>
> {
  // React component to use as a replacement.
  Block: BlockType<TProps, TAttributes> | null;
  // CSS selector to match the elements to replace.
  selector: string;
  // Function to generate the props object for the block.
  getProps?: GetPropsFn<TProps, TAttributes>;
}

export const renderFrontend = <
  TProps extends Record<string, unknown>,
  TAttributes extends Record<string, unknown>
>({
  Block,
  getProps,
  selector,
}: RenderFrontendParams<TProps, TAttributes>): void => {
  const containers = document.body.querySelectorAll(selector);
  renderBlockInContainers({ Block, containers, getProps });
};

export default renderFrontend;
